import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertTitle, Button, Snackbar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PageContainer } from '../components/AdminContentWrapper';
import UserProfileService from '../../../services/UserProfileService';
import SemanticDatabaseService from '../../../services/SemanticDatabaseService';
import SemanticTableService from '../../../services/SemanticTableService';
import SemanticModelProvisioningService from '../../../services/SemanticModelProvisioningService';
import {
  SemanticTableSection,
  SemanticTableFormDialog,
  SemanticTableViewDialog,
  SemanticTableDeleteDialog
} from '../../../components/semantic-table';

const resolveUserId = (profile) => profile?.user_id || profile?.userId || profile?.user_uuid;
const resolveAccountId = (profile) => profile?.account_id || profile?.accountUuid || profile?.account_uuid;

const asTableList = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response?.semantic_tables)) {
    return response.semantic_tables;
  }
  return [];
};

const asTableRecord = (response) => {
  if (!response) {
    return null;
  }
  if (response.table_id || response.TABLE_ID) {
    return response;
  }
  if (response.data) {
    return asTableRecord(response.data);
  }
  if (response.item) {
    return asTableRecord(response.item);
  }
  return null;
};

const asDatabaseList = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const SemanticEntities = () => {
  // Ruta anidada bajo su base semántica -- ver AppRouter.jsx
  // (semantic-models/base-datos/:databaseId/entidades). Esta pantalla ya no
  // se llega desde el menú principal, solo haciendo clic en el nombre de
  // una base semántica (ver SemanticDatabaseCard.js).
  const { databaseId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tables, setTables] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshingDatabases, setIsRefreshingDatabases] = useState(false);
  const [isRefreshingConnections, setIsRefreshingConnections] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const accountId = resolveAccountId(profile);
  const userId = resolveUserId(profile);

  const databaseIndex = useMemo(() => {
    const index = {};
    (databases || []).forEach((database) => {
      const id =
        database?.semantic_database_id ||
        database?.semanticDatabaseId ||
        database?.id ||
        database?.ID;
      if (id) {
        index[id] = database;
      }
    });
    return index;
  }, [databases]);

  const currentDatabase = databaseId ? databaseIndex[databaseId] : null;

  const activeRecords = useMemo(() => {
    return (tables || []).filter((item) => !item?.soft_delete?.deleted);
  }, [tables]);

  const deletedRecords = useMemo(() => {
    return (tables || []).filter((item) => item?.soft_delete?.deleted);
  }, [tables]);

  const hydrateRecord = useCallback(async (record) => {
    if (!record?.table_id) {
      return record;
    }
    try {
      const detailResponse = await SemanticTableService.getById(record.table_id);
      return asTableRecord(detailResponse) || record;
    } catch (detailError) {
      console.error('Error obteniendo detalle de la entidad:', detailError);
      return record;
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const profileData = await UserProfileService.getProfile();
      if (!profileData) {
        throw new Error('No se encontró el perfil del usuario.');
      }
      setProfile(profileData);

      const resolvedAccountId = resolveAccountId(profileData);
      const resolvedUserId = resolveUserId(profileData);

      if (!resolvedAccountId) {
        throw new Error('No pudimos determinar la cuenta asociada.');
      }
      if (!resolvedUserId) {
        console.warn('No pudimos determinar el usuario autenticado.');
      }

      let databasesResponse = [];
      try {
        databasesResponse = await SemanticDatabaseService.list(resolvedAccountId, { includeDeleted: false });
      } catch (databaseError) {
        console.error('Error cargando bases semánticas:', databaseError);
        setSnackbar({
          open: true,
          message: 'No fue posible cargar el listado de bases semánticas.',
          severity: 'warning'
        });
        databasesResponse = [];
      }

      let connectionsResponse = [];
      if (resolvedUserId) {
        try {
          connectionsResponse = await SemanticDatabaseService.listConnections(resolvedUserId);
        } catch (connectionsError) {
          console.error('Error cargando conexiones disponibles:', connectionsError);
          setSnackbar({
            open: true,
            message: 'No fue posible cargar las conexiones. Algunas opciones podrían no mostrarse.',
            severity: 'warning'
          });
          connectionsResponse = [];
        }
      }

      // Self-healing: catch up any file (Excel/CSV) connection whose
      // semantic_database/semantic_table never got created because the
      // browser tab that uploaded it was closed before the background poll
      // in Conexiones finished. Best-effort — failures here shouldn't block
      // loading the page.
      try {
        await SemanticModelProvisioningService.reconcileFileConnections();
      } catch (reconcileError) {
        console.error('Error al reconciliar conexiones de archivo:', reconcileError);
      }

      const tablesResponse = await SemanticTableService.list({
        accountId: resolvedAccountId,
        semanticDatabaseId: databaseId,
        includeDeleted: true
      });

      setDatabases(asDatabaseList(databasesResponse));
      setConnections(connectionsResponse || []);
      setTables(asTableList(tablesResponse));
    } catch (loadError) {
      console.error('Error cargando entidades semánticas:', loadError);
      setError(loadError.message || 'No se pudieron cargar las entidades semánticas.');
    } finally {
      setIsLoading(false);
    }
  }, [databaseId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const refreshTables = useCallback(async () => {
    if (!accountId) {
      return;
    }
    try {
      const tablesResponse = await SemanticTableService.list({
        accountId,
        semanticDatabaseId: databaseId,
        includeDeleted: true
      });
      setTables(asTableList(tablesResponse));
    } catch (refreshError) {
      console.error('Error actualizando entidades semánticas:', refreshError);
      setSnackbar({
        open: true,
        message: 'No se pudo actualizar el listado de entidades.',
        severity: 'error'
      });
    }
  }, [accountId, databaseId]);

  const refreshDatabasesList = useCallback(async () => {
    if (!accountId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar la cuenta actual para recargar las bases.',
        severity: 'error'
      });
      return;
    }
    try {
      setIsRefreshingDatabases(true);
      const response = await SemanticDatabaseService.list(accountId, { includeDeleted: false });
      setDatabases(asDatabaseList(response));
    } catch (refreshError) {
      console.error('Error actualizando listado de bases semánticas:', refreshError);
      setSnackbar({
        open: true,
        message: 'No fue posible actualizar las bases semánticas.',
        severity: 'error'
      });
    } finally {
      setIsRefreshingDatabases(false);
    }
  }, [accountId]);

  const refreshConnectionsList = useCallback(async () => {
    if (!userId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar al usuario actual para recargar las conexiones.',
        severity: 'error'
      });
      return;
    }
    try {
      setIsRefreshingConnections(true);
      const response = await SemanticDatabaseService.listConnections(userId);
      setConnections(response || []);
    } catch (refreshError) {
      console.error('Error actualizando conexiones:', refreshError);
      setSnackbar({
        open: true,
        message: 'No fue posible actualizar el listado de conexiones.',
        severity: 'error'
      });
    } finally {
      setIsRefreshingConnections(false);
    }
  }, [userId]);

  const handleOpenCreate = () => {
    setFormMode('create');
    // Preselecciona la base semántica cuando se entra acá desde su tarjeta
    // (ver SemanticDatabaseCard.js) -- SemanticTableFormDialog ya lee
    // initialValues.semantic_database_id incluso en modo "create".
    setSelectedRecord(databaseId ? { semantic_database_id: databaseId } : null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = async (record) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setIsFormOpen(true);
    try {
      const hydrated = await hydrateRecord(record);
      if (hydrated) {
        setSelectedRecord(hydrated);
      }
    } catch (hydrateError) {
      console.error('Error preparando datos para edición:', hydrateError);
      setSnackbar({
        open: true,
        message: 'No se pudieron cargar todos los datos de la entidad.',
        severity: 'warning'
      });
    }
  };

  const handleCloseForm = () => {
    if (isSaving) {
      return;
    }
    setIsFormOpen(false);
    setSelectedRecord(null);
  };

  // ImportTablesWizard (dentro de SemanticTableFormDialog) persiste las
  // tablas elegidas directo -- acá solo hace falta refrescar el listado,
  // el propio wizard ya cierra el formulario de una sola entidad.
  const handleBulkImportComplete = useCallback(() => {
    refreshTables();
    setSnackbar({ open: true, message: 'Importación de tablas completada.', severity: 'success' });
  }, [refreshTables]);

  const handleSubmitForm = async (formPayload) => {
    if (!accountId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar la cuenta asociada para guardar la entidad.',
        severity: 'error'
      });
      return;
    }

    setIsSaving(true);
    const basePayload = {
      ...formPayload,
      account_id: accountId
    };

    try {
      if (formMode === 'edit' && selectedRecord?.table_id) {
        const updateResponse = await SemanticTableService.update(selectedRecord.table_id, {
          ...basePayload,
          table_id: selectedRecord.table_id
        });
        const updatedRecord = asTableRecord(updateResponse) || {
          ...selectedRecord,
          ...basePayload
        };

        setTables((prev) =>
          prev.map((item) =>
            item.table_id === selectedRecord.table_id
              ? { ...item, ...updatedRecord }
              : item
          )
        );

        setSnackbar({ open: true, message: 'Entidad actualizada correctamente.', severity: 'success' });
      } else {
        const createResponse = await SemanticTableService.create(basePayload);
        const createdRecord = asTableRecord(createResponse);

        if (createdRecord) {
          setTables((prev) => [...prev, createdRecord]);
        } else {
          await refreshTables();
        }

        setSnackbar({ open: true, message: 'Entidad creada correctamente.', severity: 'success' });
      }

      setIsFormOpen(false);
      setSelectedRecord(null);
    } catch (submitError) {
      console.error('Error guardando entidad semántica:', submitError);
      setSnackbar({
        open: true,
        message: submitError.message || 'Ocurrió un error al guardar la entidad.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenView = async (record) => {
    setViewRecord(record);
    try {
      const hydrated = await hydrateRecord(record);
      if (hydrated) {
        setViewRecord(hydrated);
      }
    } catch (hydrateError) {
      console.error('Error obteniendo detalle de la entidad:', hydrateError);
    }
  };

  const handleCloseView = () => {
    setViewRecord(null);
  };

  const handleOpenDelete = (record) => {
    setDeleteRecord(record);
  };

  const handleCloseDelete = () => {
    if (isDeleting) {
      return;
    }
    setDeleteRecord(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRecord?.table_id) {
      return;
    }

    setIsDeleting(true);
    try {
      const deleteResponse = await SemanticTableService.softDelete(deleteRecord.table_id);
      const deletedRecord = asTableRecord(deleteResponse);

      if (deletedRecord) {
        setTables((prev) =>
          prev.map((item) =>
            item.table_id === deletedRecord.table_id ? { ...item, ...deletedRecord } : item
          )
        );
      } else {
        await refreshTables();
      }

      setSnackbar({
        open: true,
        message: 'Se realizó el borrado lógico de la entidad.',
        severity: 'success'
      });
    } catch (deleteError) {
      console.error('Error realizando borrado lógico:', deleteError);
      setSnackbar({
        open: true,
        message: deleteError.message || 'No se pudo eliminar la entidad.',
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      setDeleteRecord(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <PageContainer
      title={currentDatabase ? `Tablas de ${currentDatabase.name}` : 'Entidades semánticas'}
      description={
        currentDatabase
          ? 'Administra las tablas (entidades) que pertenecen a esta base semántica, documenta sus columnas y controla su disponibilidad.'
          : 'Administra las tablas expuestas por tus bases semánticas, documenta sus columnas y controla su disponibilidad.'
      }
      actions={[
        <Button
          key="back"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/home/semantic-models/base-datos')}
          sx={{
            color: 'var(--wb-text-label)',
            borderColor: 'var(--wb-x-border-94a3b866, rgba(148, 163, 184, 0.4))',
            '&:hover': { borderColor: 'var(--wb-text-label)' }
          }}
        >
          Volver a bases de datos
        </Button>,
        <Button
          key="create"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            bgcolor: 'var(--wb-indigo)',
            '&:hover': { bgcolor: 'var(--wb-x-bg-4f46e5, #4f46e5)' }
          }}
        >
          Nueva entidad
        </Button>
      ]}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <SemanticTableSection
        activeRecords={activeRecords}
        deletedRecords={deletedRecords}
        filter={filter}
        onFilterChange={setFilter}
        onCreate={handleOpenCreate}
        onView={handleOpenView}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDelete}
        isLoading={isLoading}
        error={null}
        databaseIndex={databaseIndex}
      />

      <SemanticTableFormDialog
        open={isFormOpen}
        mode={formMode}
        initialValues={selectedRecord}
        databases={databases}
        connections={connections}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        onBulkImportComplete={handleBulkImportComplete}
        isSaving={isSaving}
        onRefreshDatabases={accountId ? refreshDatabasesList : undefined}
        onRefreshConnections={userId ? refreshConnectionsList : undefined}
        isRefreshingDatabases={isRefreshingDatabases}
        isRefreshingConnections={isRefreshingConnections}
        accountInfo={{
          id: accountId,
          name: profile?.company_name || profile?.account_name || ''
        }}
      />

      <SemanticTableViewDialog
        open={Boolean(viewRecord)}
        onClose={handleCloseView}
        record={viewRecord}
        database={viewRecord ? databaseIndex[viewRecord.semantic_database_id] : null}
      />

      <SemanticTableDeleteDialog
        open={Boolean(deleteRecord)}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        record={deleteRecord}
        isDeleting={isDeleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default SemanticEntities;
